// Typed client for the two Worker API routes (docs/corpus-schema.md §8).
// Every call carries the Bearer token from /auth/token — the corpus API has
// no anonymous surface. Results are values: the caller branches on the
// discriminant instead of catching.

import { fetchJsonResult } from "@bindery/shared/http/json-response";
import type {
  ReadingCandidate,
  SelectionRequest,
} from "@bindery/shared/corpus/selection";
import type { TextDetail } from "@bindery/shared/corpus/text";

/**
 * Why a call failed. The distinction that matters is `not_json`: wrangler
 * serves this app with `not_found_handling: "single-page-application"`, so a
 * path the Worker does not claim in `run_worker_first` is answered with 200
 * and the HTML shell — not a 404. `res.ok` is true, `res.json()` throws, and
 * without this the failure is indistinguishable from the network being down.
 *
 * It is reachable whenever the route list and the Worker drift: a renamed
 * endpoint, a stale client against a newer deploy, a pattern that stops
 * matching. Naming it here is what lets the reason be emitted as telemetry
 * the moment this app has a client log path (`/api/log` is not ported yet).
 */
export type ApiErrorReason =
  /** fetch itself rejected — offline, DNS, TLS, aborted. */
  | "network"
  /** A non-OK status other than 401/404. */
  | "status"
  /** 200, but not JSON — the SPA fallback answered an API path. */
  | "not_json"
  /** Content-Type said JSON and the body still would not parse. */
  | "bad_json";

export type ApiResult<T> =
  | { kind: "ok"; data: T }
  | { kind: "unauthorized" }
  | { kind: "not_found" }
  | { kind: "error"; reason: ApiErrorReason };

async function call<T>(
  path: string,
  token: string,
  // Headers as a plain record, not RequestInit's HeadersInit: they are spread
  // into the request below, and spreading a Headers instance or an entry
  // array yields nothing (typescript/no-misused-spread).
  init: Omit<RequestInit, "headers"> & {
    headers?: Record<string, string>;
  } = {},
): Promise<ApiResult<T>> {
  const result = await fetchJsonResult(
    path,
    {
      ...init,
      headers: {
        ...init.headers,
        authorization: `Bearer ${token}`,
      },
    },
    (body) => body as T,
  );
  if (result.kind === "error") {
    return { kind: "error", reason: result.reason };
  }
  if (result.kind === "unauthorized" || result.kind === "not_found") {
    return { kind: result.kind };
  }
  return result;
}

/** POST /api/recommend — the three-phase selection over the reader's profile. */
export async function recommend(
  token: string,
  input: SelectionRequest,
): Promise<ApiResult<ReadingCandidate[]>> {
  const result = await call<{ candidates: ReadingCandidate[] }>(
    "/api/recommend",
    token,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return result.kind === "ok"
    ? { kind: "ok", data: result.data.candidates }
    : result;
}

/** GET /api/text/:id — the authenticated detail read, body included. */
export function getText(
  token: string,
  id: string,
): Promise<ApiResult<TextDetail>> {
  return call<TextDetail>(`/api/text/${encodeURIComponent(id)}`, token);
}
